import { useCallback } from 'react'
import { useLocation } from 'wouter'
import { useLingui } from '@lingui/react/macro'
import { ApolloError, useApolloClient, useMutation } from '@apollo/client'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'

import * as Form from '@/features/form'
import { useAuth } from '@/features/auth'

export type ClaimOrganizationProps = {
  slug: string
  secret: string
  acceptedContract: boolean
}

type ClaimOrganizationFn = (props: ClaimOrganizationProps) => Promise<void>

export function useClaimOrganization() {
  const { t } = useLingui()

  const client = useApolloClient()
  const { refresh, switchOrg } = useAuth()

  const [, navigate] = useLocation()
  const [claimOrg] = useMutation(AdminAPI.organization.CLAIM)

  const { parseApolloErrors, setServerError, errors } =
    Form.useErrorHelpers<FormData>({})

  const claim = useCallback<ClaimOrganizationFn>(
    async props => {
      return (
        claimOrg({
          variables: {
            input: props
          }
        })
          // We need to set the new organization on the session so that we can act inside of this organization
          .then(({ data, errors }) => {
            if (errors) return Promise.reject(errors)
            if (!data) return Promise.reject('unknown')

            const org = getFragment(
              SharedAPI.organization.ORGANIZATION_FRAGMENT,
              data.claimOrganization
            )

            return switchOrg(org.slug)
          })
          .then(() => {
            // We don't support multi tenancy caching currently, so let's just dump it all
            client.resetStore()
          })
          .then(() => refresh())
          .then(() => navigate('/goodies'))
          .catch(errors => {
            if (errors instanceof ApolloError) {
              parseApolloErrors(errors.graphQLErrors)

              return
            }

            if (Array.isArray(errors)) {
              parseApolloErrors(errors)

              return
            }

            setServerError(
              'unknown',
              t`Error creating organization, please try again, or reach out to our support`
            )
          })
      )
    },
    [
      claimOrg,
      switchOrg,
      refresh,
      navigate,
      parseApolloErrors,
      setServerError,
      client,
      t
    ]
  )

  return {
    claim,
    errors
  }
}
